import dayjs from "dayjs";
import { TFunction } from "i18next";
import { z } from "zod";
import { DocumentStatus, DocumentSubtype } from "utils/constants/doc.constants";
import { isNonNullable } from "utils/types/predicates";
import { isDateInFuture } from "../date.validator";

export const MAX_USER_NAME_CHARS_LIMIT = 50;
export const MAX_USER_LASTNAME_CHARS_LIMIT = 50;
export const MAX_USER_EMAIL_CHARS_LIMIT = 100;
export const MAX_USER_TITLE_CHARS_LIMIT = 255;
const MAX_POSSIBLE_DATE = "2099-12-31";
const MIN_POSSIBLE_DATE = "1900-01-01";

// Generic schema that can be reused or extended throughout the app
export const MEMBER_SCHEMA = z.object({
  label: z.string(),
  value: z.string(),
  type: z.string().optional(),
  disabled: z.boolean().optional(),
});

export const COMBO_BOX_OPTION_SCHEMA = z.object({
  label: z.string(),
  value: z.string(),
  type: z.string().optional(),
  disabled: z.boolean().optional(),
  helpText: z.string().optional(),
});

export const DOCUMENT_SCHEMA = z.object({
  id: z.string(),
  name: z.string(),
  fileType: z.string().nullish(),
  lastModifiedDate: z.string(),
  type: z.nativeEnum(DocumentSubtype),
  workflowStatus: z.string().nullish(),
  status: z.nativeEnum(DocumentStatus),
  version: z.string().nullish(),
});

export const getRequiredStringConstraint = (
  t: TFunction,
  { maxLength }: { maxLength?: number } = {},
) => {
  const schema = z
    .string()
    .trim()
    .min(1, {
      message: t("validations.requiredField", { ns: "common" }),
    });

  return isNonNullable(maxLength)
    ? schema.max(maxLength, {
        message: t("validations.characterLimit", { ns: "common" }),
      })
    : schema;
};

export const getOptionalStringConstraint = (
  t: TFunction,
  { maxLength }: { maxLength?: number } = {},
) => {
  const schema = z.string().trim();

  return (
    isNonNullable(maxLength)
      ? schema.max(maxLength, {
          message: t("validations.characterLimit", { ns: "common" }),
        })
      : schema
  ).optional();
};

export const getGenericStringArrayConstraint = (t: TFunction) =>
  z
    .string()
    .array()
    .min(1, {
      message: t("validations.requiredField", { ns: "common" }),
    });

export const getGenericEmailConstraint = (t: TFunction) =>
  getRequiredStringConstraint(t, {
    maxLength: MAX_USER_EMAIL_CHARS_LIMIT,
  }).email(t("validations.invalidEmail", { ns: "common" }));

export const getGenericSingleAssignmentConstraint = (t: TFunction) =>
  z.array(
    z.object({
      item: z.object({
        value: z.string(),
        label: z.string(),
      }),
      assignment: z.object({
        value: z.string().min(1, {
          message: t("validations.requiredAssignmentsRole", { ns: "common" }),
        }),
      }),
    }),
  );

export const getGenericMultiChipTypeaheadConstraint = (
  t: TFunction,
  { maxLength }: { maxLength: number },
) =>
  zodUniqueArray(
    z.array(
      z.object({
        value: getGenericEmailConstraint(t),
      }),
    ),
    (chips) =>
      chips.map(({ value }, index) => ({
        key: value.toLowerCase(),
        path: [index],
      })),
    { message: t("validations.duplicateEmailsFound", { ns: "common" }) },
  ).pipe(
    z
      .array(
        z.object({
          value: z.string(),
        }),
      )
      .min(1, {
        message: t("validations.requiredField", { ns: "common" }),
      })
      .max(maxLength, {
        message: t("validations.emailLimitExceeded", { ns: "common" }),
      }),
  );

export const getGenericMultiAssignmentConstraint = (
  t: TFunction,
  { maxLength }: { maxLength: number },
) =>
  zodUniqueArray(
    z
      .array(
        z.object({
          item: z.object({
            value: z.string(),
            label: z.string(),
          }),
          assignment: z.object({
            value: getGenericMultiChipTypeaheadConstraint(t, {
              maxLength,
            }),
          }),
        }),
      )
      .min(1),
    (items) =>
      items.flatMap((item, itemIndex) =>
        item.assignment.value.map((chip, chipIndex) => ({
          key: chip.value.toLowerCase(),
          path: [itemIndex, "assignment", "value", chipIndex],
        })),
      ),
    {
      message: t("validations.duplicateEmailsFoundAcrossOrganizations", {
        ns: "common",
      }),
    },
  );

export const getGenericSingleSelectTypeaheadConstraint = (t: TFunction) =>
  z
    .object({
      value: z.string(),
    })
    .nullable()
    .refine(Boolean, {
      message: t("validations.requiredField", { ns: "common" }),
    });

export const getExtendedSingleSelectTypeaheadConstraint = (t: TFunction) =>
  z
    .object({
      value: z.string(),
      label: z.string().refine((val) => val.trim().length > 0, {
        message: t("validations.requiredField", { ns: "common" }),
      }),
    })
    .nullable()
    .refine(Boolean, {
      message: t("validations.requiredField", { ns: "common" }),
    });

const getGenericRequiredDateConstraint = (t: TFunction) =>
  z.custom(
    (val) => {
      return !!val;
    },
    { message: t("validations.requiredDate", { ns: "common" }) },
  );

export const getGenericDateConstraint = (t: TFunction) =>
  getGenericRequiredDateConstraint(t).superRefine((val, ctx) => {
    if (!(val instanceof dayjs && (val as Dayjs).isValid())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("validations.invalidDate", { ns: "common" }),
      });

      return;
    }

    if ((val as Dayjs).isAfter(dayjs(MAX_POSSIBLE_DATE))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("validations.invalidDate", { ns: "common" }),
      });
    }

    if ((val as Dayjs).isBefore(dayjs(MIN_POSSIBLE_DATE))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("validations.invalidDate", { ns: "common" }),
      });
    }
  });

export const getGenericFutureDateConstraint = (t: TFunction) =>
  getGenericDateConstraint(t).superRefine((val, ctx) => {
    if (!isDateInFuture(val as Dayjs)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("validations.invalidFutureDate", { ns: "common" }),
      });
    }
  });

export const getGenericPastDateConstraint = (t: TFunction) =>
  getGenericDateConstraint(t).superRefine((val, ctx) => {
    if (isDateInFuture(val as Dayjs)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("validations.invalidPastDate", { ns: "common" }),
      });
    }
  });

export const zodUniqueArray = <
  T extends z.ZodTypeAny,
  TKey extends string,
  TPath extends (string | number)[],
>(
  schema: z.ZodArray<T, "many">,
  extract: (value: z.infer<T>[]) => Array<{ key: TKey; path: TPath }>,
  { message }: { message: string },
) => {
  return schema.superRefine((value, ctx) => {
    const pathMap = new Map<TKey, TPath[]>();

    extract(value).forEach(({ key, path }) => {
      pathMap.set(key, [...(pathMap.get(key) ?? []), path]);
    });

    Array.from(pathMap.entries())
      .filter(([_, paths]) => paths.length > 1)
      .flatMap(([_, paths]) => paths)
      .forEach((path) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message,
        });
      });
  });
};
